/**
 * Upsell Dialog Web Component
 * Displays a modal after add-to-cart showing gift progress and upsell products
 * with quantity controls and checkout CTA.
 */

const UPSELL_DIALOG_DEMO_LABELS = {
  toddler: 'Toddler',
  kids: 'Kids',
  teens: 'Teens',
  mens: 'Mens',
  womens: 'Womens',
  parents: 'Parents'
};

// Base-product demographics that trigger the Mens/Womens → Parents collapse.
const UPSELL_DIALOG_PARENTS_BASE_DEMOS = ['kids', 'teens', 'toddler'];

const UPSELL_DIALOG_BUILD = '2026-10-13-hide-equal-compare';

const UPSELL_DIALOG_KNOWN_DEMOS = ['toddler', 'kids', 'teens', 'mens', 'womens'];

class UpsellDialog extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      activeClass: 'active',
      bodyOpenClass: 'upsell-dialog-open',
      cardSelectedClass: 'upsell-dialog__upsell-card--selected',
      minusActiveClass: 'active'
    };

    // DOM elements
    this.overlay = this.querySelector('.upsell-dialog__overlay');
    this.container = this.querySelector('.upsell-dialog__container');
    this.closeBtn = this.querySelector('.upsell-dialog__close');
    this.primaryBtn = this.querySelector('[data-action="checkout"]');
    this.secondaryBtn = this.querySelector('[data-action="decline"]');

    // Gift progress elements
    this.giftSection = this.querySelector('[data-gift-section]');
    this.giftImageContainer = this.querySelector('[data-gift-image-container]');
    this.giftStatus = this.querySelector('[data-gift-status]');
    this.giftBar = this.querySelector('[data-gift-bar]');
    this.giftGoal = this.querySelector('[data-gift-goal]');
    this.giftDescription = this.querySelector('[data-gift-description]');

    // Upsell elements
    this.upsellSection = this.querySelector('[data-upsell-section]');
    this.upsellCardsContainer = this.querySelector('[data-upsell-cards]');
    this.upsellDemoTabs = this.querySelector('[data-upsell-demo-tabs]');
    this.upsellDemoEmpty = this.querySelector('[data-upsell-demo-empty]');
    this.upsellDiscountEl = this.querySelector('[data-upsell-discount]');

    // Footer price elements
    this.totalCompareEl = this.querySelector('[data-total-compare]');
    this.totalFinalEl = this.querySelector('[data-total-final]');
    this.primaryBtnLabel = this.querySelector('[data-primary-button-label]');
    this.secondaryBtnLabel = this.querySelector('[data-secondary-button-label]');

    // Callback functions
    this.onCheckoutCallback = null;
    this.onDeclineCallback = null;

    // State
    this.mainProduct = null;
    this.upsellProducts = [];
    this.giftConfig = null;
    this.isSubscription = true;
    this.upsellQuantities = {}; // { productId: quantity }
    this.activeDemo = null;
    this.previousActiveElement = null;
    this.confirmationSyncTimeout = null;
    this.demoTabsBodyObserver = null;
    this.demoTabsRefreshTimers = [];

    this.bindEvents();
  }

  connectedCallback() {
    // Support Shopify theme editor
    if (Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:select', this.onSectionSelect.bind(this));
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect.bind(this));
    }
  }

  disconnectedCallback() {
    if (Shopify && Shopify.designMode) {
      document.removeEventListener('shopify:section:select', this.onSectionSelect.bind(this));
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect.bind(this));
    }
  }

  onSectionSelect(event) {
    if (event.detail.sectionId === this.dataset.sectionId) {
      // Show with demo data in theme editor
      this.show({
        mainProduct: { price: 3900, title: 'Demo Product' },
        upsellProducts: [],
        giftConfig: { threshold: 7500 },
        isSubscription: true
      });
    }
  }

  onSectionDeselect(event) {
    if (event.detail.sectionId === this.dataset.sectionId) {
      this.hide();
    }
  }

  bindEvents() {
    // Close button click
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.decline());
    }

    // Overlay click (close when clicking outside container)
    if (this.overlay) {
      this.overlay.addEventListener('click', (event) => {
        if (event.target === this.overlay) {
          this.decline();
        }
      });
    }

    // Primary CTA click (Checkout with upsells)
    if (this.primaryBtn) {
      this.primaryBtn.addEventListener('click', () => {
        this.trackEngagement('upsell_dialog_add_to_cart', {
          engagementType: 'add_to_cart_click',
          clickText: (this.primaryBtnLabel && this.primaryBtnLabel.textContent.trim()) || 'Add to cart',
          productTitle: this.mainProduct && this.mainProduct.title
        });
        this.submitSelection();
      });
    }

    // Secondary CTA click (No thanks)
    if (this.secondaryBtn) {
      this.secondaryBtn.addEventListener('click', () => {
        this.trackEngagement('upsell_dialog_decline', {
          engagementType: 'generic_click',
          clickText: (this.secondaryBtnLabel && this.secondaryBtnLabel.textContent.trim()) || 'No thanks, continue'
        });
        this.decline();
      });
    }

    // Escape key to close
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !document.body.classList.contains('fd-info-modal-open') && this.classList.contains(this.classes.activeClass)) {
        this.decline();
      }
    });

    // Tab clicks can be re-rendered; delegation survives innerHTML swaps.
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-upsell-demo]');
      if (!tab || !this.upsellDemoTabs || !this.upsellDemoTabs.contains(tab)) return;
      if (!this.isDemoToggleEnabled()) return;

      this.activeDemo = tab.dataset.upsellDemo;
      this.renderDemoTabs();
      this.applyDemoFilter();
    });
  }

  refreshDemoTabsIfNeeded(reason) {
    if (!this.classList.contains(this.classes.activeClass)) return;

    const enabled = this.isDemoToggleEnabled();
    const tabsRendered = !!(
      this.upsellDemoTabs &&
      !this.upsellDemoTabs.hidden &&
      this.upsellDemoTabs.childElementCount > 0
    );

    if (!enabled && !tabsRendered) {
      this.logUpsell('demo-tabs:refresh-skipped', { reason, enabled, tabsRendered });
      return;
    }

    this.logUpsell('demo-tabs:refresh', { reason, enabled, tabsRendered });
    this.renderDemoTabs();
    this.applyDemoFilter();
  }

  startDemoTabsBodyWatch() {
    this.stopDemoTabsBodyWatch();

    this.demoTabsBodyObserver = new MutationObserver(() => {
      const tabsRendered = !!(
        this.upsellDemoTabs &&
        !this.upsellDemoTabs.hidden &&
        this.upsellDemoTabs.childElementCount > 0
      );

      if (this.isDemoToggleEnabled() && !tabsRendered) {
        this.refreshDemoTabsIfNeeded('body-class-mutation');
      }
    });

    this.demoTabsBodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    [0, 50, 150, 500].forEach(delay => {
      const timerId = window.setTimeout(() => {
        this.refreshDemoTabsIfNeeded('deferred-' + delay + 'ms');
      }, delay);
      this.demoTabsRefreshTimers.push(timerId);
    });
  }

  stopDemoTabsBodyWatch() {
    if (this.demoTabsBodyObserver) {
      this.demoTabsBodyObserver.disconnect();
      this.demoTabsBodyObserver = null;
    }

    this.demoTabsRefreshTimers.forEach(timerId => window.clearTimeout(timerId));
    this.demoTabsRefreshTimers = [];
  }

  isDemoToggleEnabled() {
    return document.body.classList.contains('ab-toggle-pdp');
  }

  logUpsell(stage, payload = {}) {
    console.log('[upsell]', stage, {
      build: UPSELL_DIALOG_BUILD,
      bodyClasses: document.body.className,
      abToggleOnBody: document.body.classList.contains('ab-toggle-pdp'),
      dialogClasses: this.className,
      activeDemo: this.activeDemo,
      upsellCount: this.upsellProducts?.length || 0,
      demoOrder: this.dataset.upsellDemoOrder,
      hasTabEl: !!this.upsellDemoTabs,
      tabsHidden: this.upsellDemoTabs?.hidden,
      tabsChildCount: this.upsellDemoTabs?.childElementCount || 0,
      ...payload
    });
  }

  cacheUpsellElements() {
    this.upsellSection = this.querySelector('[data-upsell-section]');
    this.upsellCardsContainer = this.querySelector('[data-upsell-cards]');
    this.upsellDemoTabs = this.querySelector('[data-upsell-demo-tabs]');
    this.upsellDemoEmpty = this.querySelector('[data-upsell-demo-empty]');
  }

  syncDemoTabsVisibilityClass() {
    const tabsVisible = !!(
      this.isDemoToggleEnabled() &&
      this.upsellDemoTabs &&
      !this.upsellDemoTabs.hidden &&
      this.upsellDemoTabs.childElementCount > 0
    );
    this.classList.toggle('upsell-dialog--demo-tabs-on', tabsVisible);
  }

  coerceDemographicText(value) {
    if (value == null || value === '') return '';

    if (Array.isArray(value)) {
      return value.map(item => this.coerceDemographicText(item)).filter(Boolean).join(' ');
    }

    if (typeof value === 'object') {
      return this.coerceDemographicText(
        value.value ?? value.label ?? value.name ?? value.handle ?? value.title ?? ''
      );
    }

    return String(value);
  }

  isKnownDemo(demo) {
    return !!demo && UPSELL_DIALOG_KNOWN_DEMOS.includes(demo);
  }

  getDemoTabOrder() {
    const settingRaw = String(this.dataset.upsellDemoOrder || '').trim();
    const raw = (settingRaw || 'toddler,kids,teens,mens,womens').split(',');
    const order = [];

    raw.forEach(slug => {
      const demo = this.normalizeUpsellDemo(slug);
      if (demo && !order.includes(demo)) order.push(demo);
    });

    return order;
  }

  normalizeUpsellDemo(value) {
    const raw = this.coerceDemographicText(value).trim().toLowerCase();
    if (!raw) return '';

    if (raw.includes('toddler')) return 'toddler';
    if (raw.includes('teen')) return 'teens';
    if (raw.includes('kid')) return 'kids';
    if (raw.includes('women') || raw === 'womens') return 'womens';
    if (raw.includes('men') || raw === 'mens') return 'mens';

    const slug = raw.replace(/[^a-z0-9]+/g, '');
    return this.isKnownDemo(slug) ? slug : '';
  }

  getUpsellDemo(product) {
    if (!product) return '';

    const sources = [
      product.demographic,
      product.customTitle,
      product.shortName,
      product.title
    ];

    for (const source of sources) {
      const demo = this.normalizeUpsellDemo(source);
      if (demo) return demo;
    }

    return '';
  }

  /**
   * The base (PDP) product's demographic. Prefers the main product's
   * custom.demographic metafield (threaded via data-main-demographic on the
   * section) and falls back to inferring it from the mainProduct fields.
   * @returns {string} normalized slug or ''
   */
  getBaseDemo() {
    const fromMetafield = this.normalizeUpsellDemo(this.dataset.mainDemographic);
    if (fromMetafield) return fromMetafield;
    return this.getUpsellDemo(this.mainProduct);
  }

  /**
   * True when the section toggle is on AND the base product's demographic is
   * one that should see Mens & Womens merged into a single "Parents" tab.
   * @returns {boolean}
   */
  shouldCollapseParents() {
    if (this.dataset.upsellCollapseParents !== 'true') return false;
    return UPSELL_DIALOG_PARENTS_BASE_DEMOS.includes(this.getBaseDemo());
  }

  /** Configurable label for the merged Mens/Womens tab. */
  getParentsLabel() {
    return this.dataset.upsellParentsLabel || UPSELL_DIALOG_DEMO_LABELS.parents;
  }

  /** Display label for a (possibly synthetic) demo slug. */
  getDemoLabel(demo) {
    if (demo === 'parents') return this.getParentsLabel();
    return UPSELL_DIALOG_DEMO_LABELS[demo] || demo;
  }

  /**
   * Map a real demo slug to the slug actually used for tabs/filtering. When the
   * Parents collapse is active, 'mens' and 'womens' both become 'parents';
   * otherwise the slug is returned unchanged.
   * @param {string} slug
   * @param {boolean} [collapse]
   * @returns {string}
   */
  effectiveDemo(slug, collapse = this.shouldCollapseParents()) {
    if (collapse && (slug === 'mens' || slug === 'womens')) return 'parents';
    return slug;
  }

  getAvailableDemos() {
    const collapse = this.shouldCollapseParents();
    const seen = new Set();

    this.upsellProducts.forEach(product => {
      const demo = this.getUpsellDemo(product);
      if (demo) seen.add(this.effectiveDemo(demo, collapse));
    });

    const ordered = [];
    this.getDemoTabOrder().forEach(demo => {
      const eff = this.effectiveDemo(demo, collapse);
      if (seen.has(eff) && !ordered.includes(eff)) ordered.push(eff);
    });

    // Parents always sits last, regardless of where Mens/Womens fell in the order.
    if (collapse) {
      const idx = ordered.indexOf('parents');
      if (idx > -1) {
        ordered.splice(idx, 1);
        ordered.push('parents');
      }
    }

    return ordered;
  }

  resolveDefaultActiveDemo(availableDemos, tabsToShow) {
    if (this.mainProduct) {
      const sourceDemo = this.effectiveDemo(this.getBaseDemo());
      if (sourceDemo && tabsToShow.includes(sourceDemo)) {
        return sourceDemo;
      }
    }

    if (availableDemos.length) return availableDemos[0];
    return tabsToShow[0] || null;
  }

  renderDemoTabs() {
    if (this.routine?.enabled) return;
    this.cacheUpsellElements();

    if (!this.upsellDemoTabs) {
      this.logUpsell('demo-tabs:skip-no-element', {
        sectionId: this.dataset.sectionId,
        childCount: this.children.length
      });
      return;
    }

    this.syncDemoTabsVisibilityClass();

    if (!this.isDemoToggleEnabled()) {
      this.logUpsell('demo-tabs:disabled-no-ab-class');
      this.upsellDemoTabs.hidden = true;
      this.upsellDemoTabs.innerHTML = '';
      this.activeDemo = null;
      if (this.upsellDemoEmpty) this.upsellDemoEmpty.hidden = true;
      this.syncDemoTabsVisibilityClass();
      return;
    }

    if (!this.upsellProducts.length) {
      this.logUpsell('demo-tabs:disabled-no-products');
      this.upsellDemoTabs.hidden = true;
      this.upsellDemoTabs.innerHTML = '';
      this.activeDemo = null;
      if (this.upsellDemoEmpty) this.upsellDemoEmpty.hidden = true;
      this.syncDemoTabsVisibilityClass();
      return;
    }

    const tabsToShow = this.getDemoTabOrder();
    const available = this.getAvailableDemos();
    const productDemos = this.upsellProducts.map(product => ({
      id: product.id,
      title: product.title,
      customTitle: product.customTitle || null,
      demographic: product.demographic ?? null,
      demographicType: product.demographic == null ? 'null' : typeof product.demographic,
      demo: this.getUpsellDemo(product)
    }));

    if (available.length < 2) {
      this.logUpsell('demo-tabs:disabled-need-2-demos-with-products', { tabsToShow, available, productDemos });
      this.upsellDemoTabs.hidden = true;
      this.upsellDemoTabs.innerHTML = '';
      this.activeDemo = available[0] || null;
      if (this.upsellDemoEmpty) this.upsellDemoEmpty.hidden = true;
      this.syncDemoTabsVisibilityClass();
      return;
    }

    if (!this.activeDemo || !available.includes(this.activeDemo)) {
      this.activeDemo = this.resolveDefaultActiveDemo(available, available);
    }

    this.upsellDemoTabs.innerHTML = available.map(demo => {
      const isActive = demo === this.activeDemo;
      const label = this.getDemoLabel(demo);
      return (
        '<button type="button"' +
        ' role="tab"' +
        ' aria-selected="' + (isActive ? 'true' : 'false') + '"' +
        ' class="fd-tabs-v3__item' + (isActive ? ' fd-tabs-v3__item--active' : '') + '"' +
        ' data-upsell-demo="' + demo + '">' +
        label +
        '</button>'
      );
    }).join('');

    this.upsellDemoTabs.hidden = false;
    this.upsellDemoTabs.removeAttribute('hidden');
    this.syncDemoTabsVisibilityClass();
    this.logUpsell('demo-tabs:rendered', { tabsToShow, available, activeDemo: this.activeDemo, productDemos });
  }

  applyDemoFilter() {
    if (this.routine?.enabled) return;
    this.cacheUpsellElements();
    if (!this.upsellCardsContainer) return;

    const enabled = this.isDemoToggleEnabled();
    const cards = Array.from(this.upsellCardsContainer.querySelectorAll('.upsell-dialog__upsell-card'));
    let visible = 0;

    cards.forEach(card => {
      let show = true;

      if (enabled && this.activeDemo) {
        show = this.effectiveDemo(card.dataset.demo) === this.activeDemo;
      }

      card.style.display = show ? '' : 'none';
      if (show) visible += 1;
    });

    if (this.upsellDemoEmpty) {
      this.upsellDemoEmpty.hidden = !enabled || visible > 0;
    }

    this.logUpsell('demo-filter', {
      enabled,
      activeDemo: this.activeDemo,
      cardCount: cards.length,
      visible,
      cardDemos: cards.map(card => ({
        id: card.dataset.productId,
        demo: card.dataset.demo,
        display: card.style.display || 'default'
      }))
    });
  }

  /**
   * Fire a GA4 event for a dialog interaction. Each interaction gets its own
   * descriptive event name; engagement_type maps to the site taxonomy
   * (generic_click / add_to_cart_click / …). Uses gtag when available, else
   * pushes to the GTM dataLayer. Params that don't apply (e.g. click_href on a
   * <button>, an absent product) are omitted.
   * @param {string} eventName - descriptive GA4 event name (e.g. 'upsell_dialog_add_to_cart')
   * @param {Object} [opts]
   * @param {string} [opts.engagementType='generic_click'] - site engagement_type taxonomy value
   * @param {string} [opts.clickText] - human-readable label of what was clicked
   * @param {string} [opts.productTitle] - product the interaction relates to
   */
  trackEngagement(eventName, { engagementType = 'generic_click', clickText, productTitle } = {}) {
    try {
      const sectionId = this.dataset.sectionId || (this.id || '').replace(/^upsell-dialog-/, '');
      const params = {
        upsell_dialog_variant: this.routine?.enabled ? 'option_a' : 'control',
        engagement_type: engagementType,
        page_section: `sections--${sectionId}__upsell-dialog`
      };
      if (this.dataset.pageType) params.page_type = this.dataset.pageType;
      if (clickText) params.click_text = clickText;
      if (productTitle) params.product_title = productTitle;

      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event: eventName }, params));
      }
      console.log('[Upsell-Dialog] GA4 event:', eventName, params);
    } catch (err) {
      console.warn('[Upsell-Dialog] trackEngagement failed:', err);
    }
  }

  /**
   * Decline and continue without upsells
   */
  decline() {
    return this.submitSelection({ decline: true });
  }

  async submitSelection(options = {}) {
    if (this.submitting) return false;
    const callback = options.decline ? this.onDeclineCallback : this.onCheckoutCallback;
    if (typeof callback !== 'function') {
      this.hide();
      return true;
    }
    this.submitting = true;
    const controls = Array.from(this.querySelectorAll('button:not(:disabled)'));
    controls.forEach(button => { button.disabled = true; });
    let error = this.querySelector('[data-upsell-error]');
    if (error) error.remove();
    try {
      const success = await callback(this.getSelectedUpsells(), options);
      if (success === false) throw new Error('Cart add failed');
      if (this.classList.contains(this.classes.activeClass)) this.hide();
      return true;
    } catch (err) {
      console.error('[Upsell-Dialog] Cart add failed:', err);
      error = document.createElement('p');
      error.dataset.upsellError = '';
      error.setAttribute('role', 'alert');
      error.textContent = window.cartStrings.error;
      this.primaryBtn.before(error);
      if (!this.classList.contains(this.classes.activeClass)) alert(window.cartStrings.error);
      return false;
    } finally {
      this.submitting = false;
      controls.forEach(button => { button.disabled = false; });
    }
  }

  getInfoModalActions(handle) {
    const product = this.upsellProducts.find(item => item.handle === handle);
    if (!product || !this.classList.contains(this.classes.activeClass)) return null;
    return {
      onAdd: () => {
        if (!this.upsellQuantities[product.id]) this.updateUpsellQuantity(String(product.id), 1);
        return this.submitSelection();
      },
      onNavigate: url => this.submitSelection({ navigateTo: url })
    };
  }

  /**
   * Show the dialog with product data
   * @param {Object} options - Configuration options
   * @param {Object} options.mainProduct - Main product being added to cart
   * @param {Array} options.upsellProducts - Array of upsell product objects
   * @param {Object} options.giftConfig - Gift threshold configuration
   * @param {boolean} options.isSubscription - Whether this is a subscription purchase
   * @param {Function} options.onCheckout - Callback when user clicks Checkout
   * @param {Function} options.onDecline - Callback when user declines
   */
  show(options = {}) {
    console.log('[upsell] dialog:show', { build: UPSELL_DIALOG_BUILD, options });

    this.clearConfirmationSyncTimeout();
    this.cacheUpsellElements();

    // Store data
    this.mainProduct = options.mainProduct || null;
    this.upsellProducts = options.upsellProducts || [];
    this.giftConfig = options.giftConfig || null;
    this.isSubscription = options.isSubscription !== false;
    this.upsellQuantities = {};
    this.routineData = options.routineData || null;
    this.routine = window.FDUpsellRoutine ? new window.FDUpsellRoutine(this) : null;
    this.routine?.prepare();

    this.logUpsell('dialog:data-ready', {
      productDemos: this.upsellProducts.map(product => ({
        id: product.id,
        title: product.title,
        demographic: product.demographic ?? null,
        demo: this.getUpsellDemo(product)
      }))
    });

    // Store callbacks
    this.onCheckoutCallback = options.onCheckout || null;
    this.onDeclineCallback = options.onDecline || null;

    // Store current focus
    this.previousActiveElement = document.activeElement;

    // Render content
    this.renderGiftSection();
    this.renderDemoTabs();
    this.renderUpsellCards();
    if (window.FDCartContext && typeof window.FDCartContext.warmPreorderTextIn === 'function') {
      window.FDCartContext.warmPreorderTextIn(this);
    }
    this.applyDemoFilter();
    this.renderButtonLabels();

    // Preselect upsell product if index provided
    const preselectedIndex = this.routine?.enabled ? -1 : (options.preselectedIndex ?? -1);
    if (preselectedIndex >= 0 && preselectedIndex < this.upsellProducts.length) {
      const productToPreselect = this.upsellProducts[preselectedIndex];
      if (productToPreselect) {
        this.updateUpsellQuantity(String(productToPreselect.id), 1);
      }
    }

    this.calculateTotals();

    const scrollable = this.querySelector('.upsell-dialog__scrollable');
    if (scrollable) scrollable.scrollTop = 0;

    // Show dialog
    this.classList.add(this.classes.activeClass);

    // Prevent body scroll
    document.body.classList.add(this.classes.bodyOpenClass);

    // Set up focus trap
    this.trapFocus();

    // Announce to screen readers
    this.setAttribute('aria-hidden', 'false');

    // Dispatch event
    this.dispatchEvent(new CustomEvent('upsell-dialog:opened'));

    // Intelligems analytics: signal that the upsell pop-up was viewed.
    try {
      window.igEvents = window.igEvents || [];
      window.igEvents.push({ event: 'viewUpsellPopUp' });
    } catch (e) {
      console.warn('[Upsell-Dialog] igEvents push failed:', e);
    }

    // GA4 / GTM: signal that the upsell dialog was viewed.
    this.trackEngagement('upsell_dialog_view', {
      engagementType: 'view',
      clickText: 'Upsell dialog viewed',
      productTitle: this.mainProduct && this.mainProduct.title
    });

    this.confirmFooterPricesAfterOpen();
    this.startDemoTabsBodyWatch();
  }

  /**
   * Hide the dialog
   */
  hide() {
    console.log('[Upsell-Dialog] Hiding dialog');

    this.clearConfirmationSyncTimeout();
    this.stopDemoTabsBodyWatch();

    // Hide dialog
    this.classList.remove(this.classes.activeClass);
    this.classList.remove('upsell-dialog--demo-tabs-on');

    // Restore body scroll
    document.body.classList.remove(this.classes.bodyOpenClass);

    // Restore focus
    if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
      this.previousActiveElement.focus();
    }

    // Update accessibility
    this.setAttribute('aria-hidden', 'true');

    // Clear callbacks
    this.onCheckoutCallback = null;
    this.onDeclineCallback = null;

    // Dispatch event
    this.dispatchEvent(new CustomEvent('upsell-dialog:closed'));
  }

  /**
   * Render the gift progress section
   */
  renderGiftSection() {
    if (!this.giftConfig || !this.giftSection) return;

    const {
      threshold,
      giftProduct,
      statusIncomplete,
      statusComplete,
      badgeOriginalPrice,
      badgeSalePrice
    } = this.giftConfig;

    // Update goal display
    if (this.giftGoal) {
      this.giftGoal.textContent = '$' + (threshold / 100).toFixed(0);
    }

    // Substitute the live threshold into the gift description. The description
    // is a single global section setting, so it carries a {threshold} token
    // (e.g. "...with orders over {threshold}") that we fill in per-product here.
    if (this.giftDescription) {
      // Cache the raw template once — on first render it still holds the token.
      if (this.giftDescriptionTemplate == null) {
        this.giftDescriptionTemplate = this.giftDescription.innerHTML;
      }
      const thresholdText = Number.isFinite(threshold)
        ? '$' + (threshold / 100).toFixed(0)
        : '';
      this.giftDescription.innerHTML = this.giftDescriptionTemplate.replace(
        /\{threshold\}/g,
        thresholdText
      );
    }

    // Render gift image if provided
    if (this.giftImageContainer && giftProduct) {
      const badgeOriginal = badgeOriginalPrice || `$${((giftProduct.comparePrice || giftProduct.price) / 100).toFixed(2)}`;
      const badgeSale = badgeSalePrice || 'FREE';

      this.giftImageContainer.innerHTML = `
        <img
          src="${giftProduct.imageUrl || ''}"
          alt="${giftProduct.title || 'Free Gift'}"
          class="upsell-dialog__gift-image"
          loading="lazy"
          width="80"
          height="90"
        >
        <div class="upsell-dialog__gift-badge">
          <span class="upsell-dialog__gift-badge-original">${badgeOriginal}</span>
          <span class="upsell-dialog__gift-badge-sale">${badgeSale}</span>
        </div>
      `;
    }

    // Initial progress update
    this.updateGiftProgress();
  }

  /**
   * Apply configurable CTA labels from the dialog config.
   */
  renderButtonLabels() {
    if (this.routine?.enabled) {
      this.primaryBtnLabel.textContent = this.routine.config.add;
      this.secondaryBtnLabel.textContent = this.routine.config.decline;
      return;
    }
    const primaryText = this.giftConfig?.primaryButtonText || this.primaryBtnLabel?.dataset.defaultText;
    const secondaryText = this.giftConfig?.secondaryButtonText || this.secondaryBtnLabel?.dataset.defaultText;

    // if (this.primaryBtnLabel && primaryText) {
    //   this.primaryBtnLabel.textContent = primaryText;
    // }

    if (this.secondaryBtnLabel && secondaryText) {
      this.secondaryBtnLabel.textContent = secondaryText;
    }
  }

  /**
   * Update gift progress bar and status text
   */
  updateGiftProgress() {
    if (!this.giftConfig) return;

    const threshold = this.giftConfig.threshold || 7500;
    const statusIncomplete = this.giftConfig.statusIncomplete || 'You are {amount} away, keep going!';
    const statusComplete = this.giftConfig.statusComplete || "You've unlocked the free gift!";

    // Calculate current total
    const currentTotal = this.getGiftProgressTotal();
    const remaining = Math.max(threshold - currentTotal, 0);
    const percentage = Math.min((currentTotal / threshold) * 100, 100);
    const isComplete = currentTotal >= threshold;

    console.log('[existingMinicartCalc] gift progress values', {
      threshold,
      currentTotal,
      remaining,
      percentage,
      isComplete
    });

    // Update progress bar
    if (this.giftBar) {
      this.giftBar.style.width = percentage + '%';
      if (isComplete) {
        this.giftBar.classList.add('upsell-dialog__gift-bar-fill--complete');
      } else {
        this.giftBar.classList.remove('upsell-dialog__gift-bar-fill--complete');
      }
    }

    if (this.giftGoal) {
      this.giftGoal.textContent = '$' + (remaining / 100).toFixed(2).replace(/\.00$/, '');
    }

    // Update status text
    if (this.routine?.enabled) this.routine.updateGift(remaining, threshold);

    if (this.giftStatus) {
      if (isComplete) {
        this.giftStatus.textContent = statusComplete;
      } else {
        const remainingFormatted = '$' + (remaining / 100).toFixed(2);
        this.giftStatus.textContent = statusIncomplete.replace('{amount}', remainingFormatted);
      }
    }
  }

  /**
   * Read the current mini-cart subtotal rendered in the flyout footer.
   * @param {'sale'|'compare'} type - Which subtotal to read
   * @returns {number|null} Total in cents
   */
  getMiniCartSubtotal(type = 'sale') {
    const parentElementId = type === 'compare'
      ? 'cartTotalPrice'
      : 'label-subscription-price';
    const directValueParent = document.getElementById(parentElementId);

    if (directValueParent) {
      const childElements = directValueParent.children;
      const directValueElement = childElements.length > 1
        ? childElements[1]
        : directValueParent.querySelector('[aria-hidden="true"]');
      const directPriceText = directValueElement?.textContent?.trim() || '';
      const directCents = this.parseMoneyToCents(directPriceText);

      console.log('[existingMinicartCalc] mini-cart subtotal read from parent id', {
        type,
        parentElementId,
        childCount: childElements.length,
        usedSecondChild: childElements.length > 1,
        priceText: directPriceText,
        cents: directCents
      });

      return Number.isFinite(directCents) ? directCents : null;
    }

    const subtotalRow = document.querySelector(
      'aside#main-nav .firstday-cart__flyout-footer .firstday-cart__flyout-subtotal.original-price'
    );
    if (!subtotalRow) {
      console.log('[existingMinicartCalc] mini-cart subtotal row not found', { type, parentElementId });
      return null;
    }

    const amountContainer = subtotalRow.querySelector('.firstday-cart__flyout-subtotal-amount');
    if (!amountContainer) {
      console.log('[existingMinicartCalc] mini-cart amount container not found', { type, parentElementId });
      return null;
    }

    const selector = type === 'compare'
      ? '.mini-cart-original-price [aria-hidden="true"], .mini-cart-original-price'
      : '.mini-cart-price.sale [aria-hidden="true"], .mini-cart-price.sale';

    const matchedElement = amountContainer.querySelector(selector);
    const priceText = matchedElement?.textContent?.trim()
      || amountContainer.textContent?.trim()
      || '';
    const cents = this.parseMoneyToCents(priceText);

    console.log('[existingMinicartCalc] mini-cart subtotal read', {
      type,
      parentElementId,
      selector,
      matched: Boolean(matchedElement),
      priceText,
      cents
    });

    return Number.isFinite(cents) ? cents : null;
  }

  /**
   * Get gift progress total in cents, including the existing mini-cart subtotal.
   * This is used only for the gift progress UI, not the dialog CTA totals.
   * @returns {number}
   */
  getGiftProgressTotal() {
    const existingCartTotal = this.getMiniCartSubtotal('sale') || 0;
    const dialogTotal = this.getCurrentTotal();
    const total = existingCartTotal + dialogTotal;

    console.log('[existingMinicartCalc] gift progress total computed', {
      existingCartTotal,
      dialogTotal,
      total
    });

    return total;
  }

  /**
   * Get current total in cents (main product + upsells)
   */
  getCurrentTotal() {
    const liveSummaryTotal = this.getLiveSummarySubtotal('PBFCM-breakdown-subtotal-final');
    let total = liveSummaryTotal;
    let usedFallbackMainProduct = false;

    // Fall back to the dialog's local product data if the PDP subtotal isn't available.
    if (total === null) {
      total = 0;
      usedFallbackMainProduct = true;

      if (this.mainProduct) {
        const mainPrice = this.isSubscription
          ? (this.mainProduct.subscriptionPrice || this.mainProduct.price)
          : this.mainProduct.price;
        const mainQty = this.mainProduct.quantity || 1;
        total += mainPrice * mainQty;
      }
    }

    // Add upsell prices
    Object.keys(this.upsellQuantities).forEach(productId => {
      const qty = this.upsellQuantities[productId];
      if (qty > 0) {
        const product = this.upsellProducts.find(p => String(p.id) === String(productId));
        if (product) {
          const price = this.isSubscription
            ? (product.subscriptionPrice || product.price)
            : (product.onetimePrice || product.price);
          total += price * qty;
        }
      }
    });

    console.log('[existingMinicartCalc] current total computed', {
      liveSummaryTotal,
      usedFallbackMainProduct,
      upsellQuantities: this.upsellQuantities,
      total
    });

    return total;
  }

  /**
   * Get current original total in cents (without discounts)
   */
  getOriginalTotal() {
    const liveSummaryCompareTotal = this.getLiveSummarySubtotal('PBFCM-breakdown-subtotal-compare');
    let total = liveSummaryCompareTotal;
    let usedFallbackMainProduct = false;

    // Fall back to the dialog's local product data if the PDP compare subtotal isn't available.
    if (total === null) {
      total = 0;
      usedFallbackMainProduct = true;

      if (this.mainProduct) {
        const mainPrice = this.mainProduct.comparePrice || this.mainProduct.price;
        const mainQty = this.mainProduct.quantity || 1;
        total += mainPrice * mainQty;
      }
    }

    // Add upsell original prices
    Object.keys(this.upsellQuantities).forEach(productId => {
      const qty = this.upsellQuantities[productId];
      if (qty > 0) {
        const product = this.upsellProducts.find(p => String(p.id) === String(productId));
        if (product) {
          const price = product.comparePrice || product.price;
          total += price * qty;
        }
      }
    });

    console.log('[existingMinicartCalc] original total computed', {
      liveSummaryCompareTotal,
      usedFallbackMainProduct,
      upsellQuantities: this.upsellQuantities,
      total
    });

    return total;
  }

  /**
   * Get the unlocked gift's original price in cents.
   * @returns {number}
   */
  getGiftOriginalTotal() {
    if (!this.giftConfig) return 0;

    const threshold = this.giftConfig.threshold || 7500;
    const currentTotal = this.getCurrentTotal();

    if (currentTotal < threshold) {
      return 0;
    }

    const badgeOriginalCents = this.parseMoneyToCents(this.giftConfig.badgeOriginalPrice);
    if (Number.isFinite(badgeOriginalCents) && badgeOriginalCents > 0) {
      return badgeOriginalCents;
    }

    const giftProduct = this.giftConfig.giftProduct;
    if (!giftProduct) return 0;

    return giftProduct.comparePrice || giftProduct.price || 0;
  }

  /**
   * Read a subtotal value already rendered in the PDP summary.
   * @param {string} elementId - Summary subtotal element ID
   * @returns {number|null} Total in cents
   */
  getLiveSummarySubtotal(elementId) {
    const subtotalEl = document.getElementById(elementId);
    if (!subtotalEl) return null;

    const priceText = subtotalEl.querySelector('[aria-hidden="true"]')?.textContent || subtotalEl.textContent;
    const cents = this.parseMoneyToCents(priceText);

    return Number.isFinite(cents) ? cents : null;
  }

  /**
   * Get the rendered money string from a live PDP subtotal element.
   * @param {string} elementId - Summary subtotal element ID
   * @returns {string}
   */
  getLiveSummarySubtotalText(elementId) {
    const subtotalEl = document.getElementById(elementId);
    if (!subtotalEl) return '';

    return subtotalEl.querySelector('[aria-hidden="true"]')?.textContent?.trim()
      || subtotalEl.textContent?.trim()
      || '';
  }

  /**
   * Parse a money string like "$46.41" into cents.
   * @param {string} value - Money string
   * @returns {number|null} Amount in cents
   */
  parseMoneyToCents(value) {
    if (!value) return null;

    const normalized = String(value).replace(/[^0-9.-]/g, '');
    if (!normalized) return null;

    const amount = Number.parseFloat(normalized);
    if (!Number.isFinite(amount)) return null;

    return Math.round(amount * 100);
  }

  /**
   * Clear any pending confirmation sync timer.
   */
  clearConfirmationSyncTimeout() {
    if (this.confirmationSyncTimeout) {
      clearTimeout(this.confirmationSyncTimeout);
      this.confirmationSyncTimeout = null;
    }
  }

  /**
   * Log the source/footer price state and resync the footer totals.
   * @param {string} stage - Log stage label
   */
  logAndSyncFooterTotals(stage) {
    const sourceCompareText = this.getLiveSummarySubtotalText('PBFCM-breakdown-subtotal-compare');
    const sourceFinalText = this.getLiveSummarySubtotalText('PBFCM-breakdown-subtotal-final');
    const footerCompareText = this.totalCompareEl?.textContent?.trim() || '';
    const footerFinalText = this.totalFinalEl?.textContent?.trim() || '';

    console.log('[Confirmation-upsell-price]', stage, {
      source: {
        compare: sourceCompareText || null,
        final: sourceFinalText || null
      },
      footerBefore: {
        compare: footerCompareText || null,
        final: footerFinalText || null
      }
    });

    this.calculateTotals();

    console.log('[Confirmation-upsell-price]', `${stage}:after-calculate`, {
      footerAfter: {
        compare: this.totalCompareEl?.textContent?.trim() || null,
        final: this.totalFinalEl?.textContent?.trim() || null
      }
    });
  }

  /**
   * Reconfirm footer prices right after open and again after a short delay.
   */
  confirmFooterPricesAfterOpen() {
    this.logAndSyncFooterTotals('immediate');

    this.confirmationSyncTimeout = setTimeout(() => {
      this.logAndSyncFooterTotals('after-1ms');
      this.confirmationSyncTimeout = null;
    }, 1);
  }

  /**
   * Render upsell product cards
   */
  renderUpsellCards() {
    if (this.routine?.enabled) return this.routine.render();
    if (!this.upsellCardsContainer) return;

    if (!this.upsellProducts || this.upsellProducts.length === 0) {
      this.upsellCardsContainer.innerHTML = '';
      if (this.upsellSection) {
        this.upsellSection.style.display = 'none';
      }
      return;
    }

    if (this.upsellSection) {
      this.upsellSection.style.display = '';
    }

    const cardsHTML = this.upsellProducts.map(product => this.renderUpsellCard(product)).join('');
    this.upsellCardsContainer.innerHTML = cardsHTML;
    this.hideEqualPriceCompares();
    requestAnimationFrame(() => this.hideEqualPriceCompares());
    setTimeout(() => this.hideEqualPriceCompares(), 250);

    // Bind quantity button events
    this.upsellCardsContainer.querySelectorAll('.upsell-dialog__qty-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const productId = btn.dataset.productId;
        const action = btn.dataset.action;
        const delta = action === 'increase' ? 1 : -1;
        // Track each +/- click (real clicks only — not the programmatic preselect)
        // so we can see which upsell product titles are being engaged with.
        const product = this.upsellProducts.find(p => String(p.id) === String(productId));
        this.trackEngagement(
          action === 'increase' ? 'upsell_dialog_quantity_increase' : 'upsell_dialog_quantity_decrease',
          {
            engagementType: 'generic_click',
            clickText: action === 'increase' ? 'Increase upsell quantity' : 'Decrease upsell quantity',
            productTitle: product && product.title
          }
        );
        this.updateUpsellQuantity(productId, delta);
      });
    });

    this.applyDemoFilter();
  }

  hideEqualPriceCompares() {
    this.querySelectorAll('.upsell-dialog__upsell-price-box').forEach((box) => {
      const sale = box.querySelector('.upsell-dialog__upsell-price-final');
      if (!sale) return;
      const saleMatch = String(sale.textContent || '').replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
      const saleAmt = saleMatch ? Number(saleMatch[1]) : NaN;
      if (!Number.isFinite(saleAmt)) return;
      box.querySelectorAll('.upsell-dialog__upsell-price-compare, s, .igComparePrice').forEach((el) => {
        if (el === sale) return;
        const match = String(el.textContent || '').replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
        const amt = match ? Number(match[1]) : NaN;
        if (Number.isFinite(amt) && Math.abs(amt - saleAmt) < 0.009) {
          el.hidden = true;
          el.style.setProperty('display', 'none', 'important');
        }
      });
    });
  }

  /**
   * Render a single upsell card
   * @param {Object} product - Product object
   * @returns {string} HTML string
   */
  renderUpsellCard(product) {
    const price = this.isSubscription
      ? (product.subscriptionPrice || product.price)
      : (product.onetimePrice || product.price);
    const comparePrice = product.comparePrice || product.price;
    const quantity = this.upsellQuantities[product.id] || 0;
    const isSelected = quantity > 0;
    const maxQty = product.maxQuantity || 5;

    const priceFormatted = '$' + (price / 100).toFixed(2);
    const compareFormatted = '$' + (comparePrice / 100).toFixed(2);
    const hasDiscount = Number(comparePrice) > Number(price);
    const variantId = product.variantId || '';
    const variantAttr = hasDiscount && variantId ? ` data-variant-id="${variantId}"` : '';
    const saleMultiplier = hasDiscount && comparePrice > 0 ? price / comparePrice : null;
    const multiplierAttr = saleMultiplier != null && saleMultiplier < 0.9999
      ? ` data-price-multiplier="${saleMultiplier}"`
      : '';

    // Discount % badge shown on the product image (reflects the active
    // subscription/one-time price vs the compare-at price). Hidden when there's
    // no saving.
    const badgeEnabled = this.dataset.discountBadgeEnabled === 'true';
    const discountPct = comparePrice > 0 && comparePrice > price
      ? Math.round((comparePrice - price) / comparePrice * 100)
      : 0;
    const discountBadge = (badgeEnabled && discountPct > 0)
      ? `<span class="upsell-dialog__upsell-discount-badge" aria-hidden="true">${discountPct}%</span>`
      : '';

    const description = product.description
      ? `<p class="upsell-dialog__upsell-description">${product.description}</p>`
      : '';

    // Title priority: the per-card custom title from the upsell-card metaobject,
    // then the product's custom.short_name metafield, then the full product title.
    const fullTitle = product.title || 'Product';
    const shortName = product.shortName || '';
    const customTitle = product.customTitle || '';
    const displayTitle = customTitle || shortName || fullTitle;

    // Optional per-card "more info" link → opens the shared product info modal
    // (window.FDInfoModal, rendered globally in theme.liquid). Gated on the
    // section setting (data-upsell-info-enabled) and the product having a handle.
    const infoEnabled = this.dataset.upsellInfoEnabled === 'true';
    const infoLabel = this.dataset.upsellInfoLabel || 'Learn More';
    // The info panel shows the offer the card shows (promo price, compare-at, plan),
    // not the product's base price.
    const infoPlanLabel = this.isSubscription ? 'Delivered monthly' : 'One-time purchase';
    const infoOfferAttrs = ` data-info-modal-price="${Math.round(price)}" data-info-modal-compare="${Math.round(comparePrice)}" data-info-modal-plan="${infoPlanLabel}"`;
    const infoLink = (infoEnabled && product.handle)
      ? `<button type="button" class="upsell-dialog__upsell-info-link" data-info-modal-open="${product.handle}"${infoOfferAttrs}>${infoLabel}</button>`
      : '';

    const selectedClass = isSelected ? this.classes.cardSelectedClass : '';
    const minusActiveClass = quantity > 0 ? this.classes.minusActiveClass : '';
    const unlockHtml = this.renderUnlockAttachment(product.freeUnlock);
    const hasUnlockClass = unlockHtml ? 'upsell-dialog__upsell-card--has-unlock' : '';
    const demoSlug = this.getUpsellDemo(product);

    return `
      <div class="upsell-dialog__upsell-card ${selectedClass} ${hasUnlockClass}" data-product-id="${product.id}" data-demo="${demoSlug}">
        <div class="upsell-dialog__upsell-product">
          ${product.imageUrl ? `
            <div class="upsell-dialog__upsell-image">
              ${discountBadge}
              <img
                src="${product.imageUrl}"
                alt="${product.title || ''}"
                class="upsell-dialog__upsell-img"
                loading="lazy"
                width="48"
                height="48"
              >
            </div>
          ` : ''}
          <div class="upsell-dialog__upsell-info">
            <h4 class="upsell-dialog__upsell-title">${displayTitle}</h4>
            ${description}
            ${infoLink}
          </div>
        </div>
        <div class="upsell-dialog__upsell-controls">
          <div class="upsell-dialog__upsell-price-box">
            ${hasDiscount ? `<span class="upsell-dialog__upsell-price-compare">${compareFormatted}</span>` : ''}
            <span class="upsell-dialog__upsell-price-final${hasDiscount ? ' igPrice' : ''}"${variantAttr}${multiplierAttr}>${priceFormatted}</span>
          </div>
          <div class="upsell-dialog__upsell-quantity" role="group" aria-label="Quantity for ${product.title || 'product'}">
            <button
              type="button"
              class="upsell-dialog__qty-btn upsell-dialog__qty-btn--minus ${minusActiveClass}"
              data-product-id="${product.id}"
              data-action="decrease"
              aria-label="Decrease quantity"
              ${quantity === 0 ? 'disabled' : ''}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <span class="upsell-dialog__qty-display" aria-live="polite" aria-atomic="true">${quantity}</span>
            <button
              type="button"
              class="upsell-dialog__qty-btn upsell-dialog__qty-btn--plus"
              data-product-id="${product.id}"
              data-action="increase"
              aria-label="Increase quantity"
              ${quantity >= maxQty ? 'disabled' : ''}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
        <input type="hidden"
          class="upsell-dialog__upsell-data"
          data-product-id="${product.id}"
          data-variant-id="${product.variantId}"
          data-price="${price}"
          data-compare-price="${comparePrice}"
          data-is-addon="${product.isAddon ? 'true' : 'false'}"
          data-title="${product.title || ''}"
          data-max="${maxQty}"
        >
        ${unlockHtml}
      </div>
    `;
  }

  /**
   * Render the free "unlocks" attachment beneath an upsell card.
   * Shows a separate product offered free with this item (no quantity stepper —
   * its quantity follows the trigger upsell and it's added to cart by the
   * controller). Returns '' when the product has no freeUnlock configured.
   * @param {Object|null} unlock - product.freeUnlock from the Liquid JSON
   * @returns {string} HTML string
   */
  renderUnlockAttachment(unlock) {
    if (!unlock || !unlock.variantId) return '';

    const compare = unlock.comparePrice || 0;
    const compareFormatted = '$' + (compare / 100).toFixed(2);
    const unlockVariantAttr = unlock.variantId ? ` data-variant-id="${unlock.variantId}"` : '';
    const label = unlock.label || 'UNLOCKS';
    const note = unlock.note || 'Added to your order with this item';
    const title = `${unlock.title || 'Free gift'}`;

    return `
      <div class="upsell-dialog__unlock">
        <div class="upsell-dialog__unlock-divider">
          <span class="upsell-dialog__unlock-badge">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M13 5.5H3a.5.5 0 0 0-.5.5v1.5h11V6a.5.5 0 0 0-.5-.5Z" fill="currentColor"/>
              <path d="M3 8.5V13a.5.5 0 0 0 .5.5H7.5V8.5H3ZM8.5 13.5H12.5a.5.5 0 0 0 .5-.5V8.5H8.5V13.5Z" fill="currentColor"/>
              <path d="M8 5.5C8 5.5 7.5 2.5 5.75 2.5a1.25 1.25 0 0 0 0 2.5M8 5.5C8 5.5 8.5 2.5 10.25 2.5a1.25 1.25 0 0 1 0 2.5" stroke="currentColor" stroke-width="1" stroke-linecap="round" fill="none"/>
            </svg>
            ${label}
          </span>
        </div>
        <div class="upsell-dialog__unlock-row">
          <div class="upsell-dialog__unlock-product">
            ${unlock.imageUrl ? `
              <div class="upsell-dialog__unlock-image">
                <img src="${unlock.imageUrl}" alt="${unlock.title || ''}" loading="lazy" width="40" height="40">
              </div>
            ` : ''}
            <div class="upsell-dialog__unlock-info">
              <h4 class="upsell-dialog__unlock-title">${title}</h4>
              <p class="upsell-dialog__unlock-note">${note}</p>
            </div>
          </div>
          <div class="upsell-dialog__unlock-price">
            ${compare > 0 ? `
              <span class="upsell-dialog__unlock-price-compare igPrice"${unlockVariantAttr}>${compareFormatted}</span>
            ` : ''}
            <span class="upsell-dialog__unlock-price-free">FREE</span>
            <span class="upsell-dialog__unlock-added">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              ADDED
            </span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Update upsell product quantity
   * @param {string} productId - Product ID
   * @param {number} delta - Change in quantity (+1 or -1)
   */
  updateUpsellQuantity(productId, delta) {
    const product = this.upsellProducts.find(p => String(p.id) === String(productId));
    if (!product) return;

    const maxQty = product.maxQuantity || 5;
    const currentQty = this.upsellQuantities[productId] || 0;
    let newQty = currentQty + delta;

    // Clamp between 0 and max
    newQty = Math.max(0, Math.min(newQty, maxQty));

    // Update state
    if (newQty === 0) {
      delete this.upsellQuantities[productId];
    } else {
      this.upsellQuantities[productId] = newQty;
    }


    // Update card UI
    const card = this.upsellCardsContainer.querySelector(`[data-product-id="${productId}"]`);
    if (card) {
      // Update quantity display
      const qtyDisplay = card.querySelector('.upsell-dialog__qty-display');
      if (qtyDisplay) qtyDisplay.textContent = newQty;

      // Update minus button state
      const minusBtn = card.querySelector('[data-action="decrease"]');
      if (minusBtn) {
        minusBtn.disabled = newQty === 0;
        if (newQty > 0) {
          minusBtn.classList.add(this.classes.minusActiveClass);
        } else {
          minusBtn.classList.remove(this.classes.minusActiveClass);
        }
      }

      // Update plus button state
      const plusBtn = card.querySelector('[data-action="increase"]');
      if (plusBtn) {
        plusBtn.disabled = newQty >= maxQty;
      }

      // Update selected state
      if (newQty > 0) {
        card.classList.add(this.classes.cardSelectedClass);
      } else {
        card.classList.remove(this.classes.cardSelectedClass);
      }
    }

    this.routine?.syncQuantities();

    // Recalculate totals and gift progress
    this.calculateTotals();
    this.updateGiftProgress();

    console.log('[Upsell-Dialog] Updated quantity:', productId, newQty);
  }

  /**
   * Calculate and update footer totals
   */
  calculateTotals() {
    const currentTotal = this.getCurrentTotal();
    const originalTotal = this.getOriginalTotal() + this.getGiftOriginalTotal();

    const currentFormatted = '$' + (currentTotal / 100).toFixed(2);
    const originalFormatted = '$' + (originalTotal / 100).toFixed(2);

    if (this.totalFinalEl) {
      this.totalFinalEl.textContent = currentFormatted;
    }

    if (this.totalCompareEl) {
      if (originalTotal > currentTotal) {
        this.totalCompareEl.textContent = originalFormatted;
        this.totalCompareEl.style.display = '';
      } else {
        this.totalCompareEl.style.display = 'none';
      }
    }
  }

  /**
   * Get selected upsells for cart
   * @returns {Object} Map of productId to { quantity, variantId, price, title, isAddon }
   */
  getSelectedUpsells() {
    const selected = {};

    Object.keys(this.upsellQuantities).forEach(productId => {
      const qty = this.upsellQuantities[productId];
      if (qty > 0) {
        const product = this.upsellProducts.find(p => String(p.id) === String(productId));
        if (product) {
          const price = this.isSubscription
            ? (product.subscriptionPrice || product.price)
            : (product.onetimePrice || product.price);

          selected[productId] = {
            quantity: qty,
            variantId: product.variantId,
            price: price,
            comparePrice: product.comparePrice || product.price,
            title: product.title,
            isAddon: product.isAddon || false,
            coupon: product.coupon || '',
            sellingPlanId: product.sellingPlanId || null,
            // Bundle/starter-pack upsells carry their component variants so the
            // controller can expand them into multiple line items (mirrors the
            // PDP main-product starter flow). Empty/absent for single-variant upsells.
            bundleName: product.bundleName || '',
            bundleVariants: Array.isArray(product.bundleVariants) ? product.bundleVariants : [],
            // Parent product of a bundle upsell, so the mini-cart group can link back to it.
            productId: productId,
            handle: product.handle || '',
            // Free "unlocks" attachment (separate product added free with this
            // item). Controller adds it as its own line at promo price 0.
            freeUnlock: product.freeUnlock || null
          };
        }
      }
    });

    return this.routine?.enabled ? this.routine.expandSelection(selected) : selected;
  }

  /**
   * Trap focus within the dialog for accessibility
   */
  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    if (this.focusTrapHandler) this.removeEventListener('keydown', this.focusTrapHandler);

    // Focus the first focusable element
    firstFocusable.focus();

    // Handle tab key to keep focus within dialog
    const handleTabKey = (event) => {
      if (event.key !== 'Tab' || document.body.classList.contains('fd-info-modal-open')) return;
      if (!this.classList.contains(this.classes.activeClass)) return;

      const visible = Array.from(this.querySelectorAll('button:not(:disabled), [href], [tabindex="0"]')).filter(el => el.getClientRects().length);
      const firstFocusable = visible[0];
      const lastFocusable = visible[visible.length - 1];
      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    this.focusTrapHandler = handleTabKey;
    this.addEventListener('keydown', handleTabKey);
  }
}

// Define the custom element
if (!customElements.get('upsell-dialog')) {
  console.log('[upsell] upsell-dialog.js loaded', UPSELL_DIALOG_BUILD);
  customElements.define('upsell-dialog', UpsellDialog);
}
